'use client';

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type * as THREE_NS from 'three';
import { getRal, type CalculatorState } from './calculator-config';

type Three = typeof import('three');

export interface Building3DHandle {
  /** Снять текущий кадр 3D-сцены в data URL (JPEG). null, если 3D ещё не готов. */
  capture: () => string | null;
}

/** Удаляет и освобождает все объекты группы. */
function disposeGroup(group: THREE_NS.Group) {
  while (group.children.length) {
    const obj = group.children[0] as THREE_NS.Mesh;
    if (obj.geometry) obj.geometry.dispose();
    const material = obj.material as THREE_NS.Material | THREE_NS.Material[] | undefined;
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else if (material) material.dispose();
    group.remove(obj);
  }
}

/**
 * Параметрическое построение здания — порт draw() из met-panel.ru.
 * Корпус из RAL-цвета, двускатная/односкатная кровля, окна на длинных стенах,
 * ворота на торцах. Вызывается заново при каждом изменении параметров.
 */
function buildModel(THREE: Three, group: THREE_NS.Group, state: CalculatorState) {
  disposeGroup(group);

  const { width: w, length: l, height: h, roof, windows: win, gates: door } = state;
  const ral = getRal(state.ral);

  const mWall = new THREE.MeshStandardMaterial({ color: ral.hex, roughness: 0.5, metalness: 0.1 });
  const mRoof = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.6 });
  const mGlass = new THREE.MeshStandardMaterial({ color: 0x2c3e50, metalness: 0.9, roughness: 0.0 });
  const mFrame = new THREE.MeshStandardMaterial({ color: 0x2d3436 });
  const mDoor = new THREE.MeshStandardMaterial({ color: 0x374151, roughness: 0.4 });
  const mBase = new THREE.MeshStandardMaterial({ color: 0x9ca3af });

  // Фундаментная плита
  const slab = new THREE.Mesh(new THREE.BoxGeometry(w + 1, 0.2, l + 1), mBase);
  slab.receiveShadow = true;
  group.add(slab);

  // Корпус
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, l), mWall);
  box.position.y = h / 2;
  box.castShadow = true;
  box.receiveShadow = true;
  group.add(box);

  // Кровля
  if (roof === 'double') {
    const rise = w * 0.15;
    const shp = new THREE.Shape();
    shp.moveTo(-w / 2, 0);
    shp.lineTo(w / 2, 0);
    shp.lineTo(0, rise);
    const geoT = new THREE.ExtrudeGeometry(shp, { depth: l, bevelEnabled: false });
    geoT.translate(0, h, -l / 2);
    const gables = new THREE.Mesh(geoT, mWall);
    gables.castShadow = true;
    group.add(gables);

    const len = Math.hypot(w / 2, rise) + 0.6;
    const ang = Math.atan(rise / (w / 2));
    const r1 = new THREE.Mesh(new THREE.BoxGeometry(len, 0.15, l + 0.6), mRoof);
    r1.position.set(-w / 4, h + rise / 2, 0);
    r1.rotation.z = ang;
    const r2 = r1.clone();
    r2.position.set(w / 4, h + rise / 2, 0);
    r2.rotation.z = -ang;
    group.add(r1, r2);
  } else {
    const rise = 2;
    const shp = new THREE.Shape();
    shp.moveTo(-w / 2, 0);
    shp.lineTo(w / 2, 0);
    shp.lineTo(w / 2, rise);
    shp.lineTo(-w / 2, 0);
    const geoT = new THREE.ExtrudeGeometry(shp, { depth: l, bevelEnabled: false });
    geoT.translate(0, h, -l / 2);
    const gables = new THREE.Mesh(geoT, mWall);
    group.add(gables);

    const rf = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 0.2, l + 0.6), mRoof);
    rf.position.set(0, h + rise / 2, 0);
    rf.rotation.z = Math.atan(rise / w);
    group.add(rf);
  }

  // Окна — на длинных стенах
  if (win > 0) {
    const cnt = Math.ceil(win / 2);
    const sp = l / (cnt + 1);
    const winW = 2.0;
    const winH = 1.2;
    for (let s = 0; s < 2; s++) {
      const x = (s === 0 ? w / 2 : -w / 2) + (s === 0 ? 0.05 : -0.05);
      for (let i = 0; i < cnt; i++) {
        if (s * cnt + i >= win) break;
        const z = -l / 2 + sp * (i + 1);
        const g = new THREE.Mesh(new THREE.BoxGeometry(0.1, winH, winW), mGlass);
        g.position.set(x, h * 0.6, z);
        group.add(g);
        const f = new THREE.Mesh(new THREE.BoxGeometry(0.12, winH + 0.2, winW + 0.2), mFrame);
        f.position.set(x, h * 0.6, z);
        group.add(f);
      }
    }
  }

  // Ворота — на торцах
  if (door > 0) {
    const cnt = Math.ceil(door / 2);
    const sp = w / (cnt + 1);
    const dW = 3.6;
    const dH = 4.2;
    for (let s = 0; s < 2; s++) {
      const z = (s === 0 ? l / 2 : -l / 2) + (s === 0 ? 0.05 : -0.05);
      const localCount = s === 0 ? cnt : door - cnt;
      for (let i = 0; i < localCount; i++) {
        const x = -w / 2 + sp * (i + 1);
        const d = new THREE.Mesh(new THREE.BoxGeometry(dW, dH, 0.1), mDoor);
        d.position.set(x, dH / 2, z);
        group.add(d);
        const f = new THREE.Mesh(new THREE.BoxGeometry(dW + 0.4, dH + 0.2, 0.15), mFrame);
        f.position.set(x, dH / 2, z);
        group.add(f);
      }
    }
  }
}

/**
 * 3D-визуализация здания (этап 2). three.js грузится динамически (только клиент),
 * поэтому не утяжеляет главную. До загрузки / при отсутствии WebGL показываем fallback.
 */
const Building3D = forwardRef<Building3DHandle, {
  state: CalculatorState;
  fallback?: React.ReactNode;
}>(function Building3D({ state, fallback }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const threeRef = useRef<Three | null>(null);
  const rendererRef = useRef<THREE_NS.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE_NS.Scene | null>(null);
  const cameraRef = useRef<THREE_NS.PerspectiveCamera | null>(null);
  const modelGroupRef = useRef<THREE_NS.Group | null>(null);
  const frameRef = useRef<number>(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Снимок текущего кадра для вставки в PDF. Рендерим синхронно перед чтением
  // буфера — это работает без preserveDrawingBuffer.
  useImperativeHandle(ref, () => ({
    capture: () => {
      const r = rendererRef.current;
      const s = sceneRef.current;
      const c = cameraRef.current;
      if (!r || !s || !c) return null;
      try {
        r.render(s, c);
        return r.domElement.toDataURL('image/jpeg', 0.92);
      } catch {
        return null;
      }
    },
  }), []);

  // Инициализация сцены (один раз)
  useEffect(() => {
    let disposed = false;
    const cleanupFns: Array<() => void> = [];

    (async () => {
      try {
        const THREE = await import('three');
        if (disposed || !canvasRef.current || !containerRef.current) return;
        threeRef.current = THREE;

        const canvas = canvasRef.current;
        const box = containerRef.current;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xdbeafe);
        scene.fog = new THREE.Fog(0xdbeafe, 100, 300);
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(45, 1, 1, 1000);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current = renderer;

        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const sun = new THREE.DirectionalLight(0xffffff, 0.85);
        sun.position.set(60, 100, 50);
        sun.castShadow = true;
        sun.shadow.mapSize.width = 1024;
        sun.shadow.mapSize.height = 1024;
        scene.add(sun);

        const ground = new THREE.Mesh(
          new THREE.PlaneGeometry(500, 500),
          new THREE.MeshStandardMaterial({ color: 0x7cb342 }),
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.2;
        ground.receiveShadow = true;
        scene.add(ground);

        const modelGroup = new THREE.Group();
        scene.add(modelGroup);
        modelGroupRef.current = modelGroup;

        // Управление: вращение мышью / пальцем, зум колесом
        let isDrag = false;
        let lx = 0;
        const onMouseDown = (e: MouseEvent) => {
          isDrag = true;
          lx = e.clientX;
        };
        const onMouseUp = () => {
          isDrag = false;
        };
        const onMouseMove = (e: MouseEvent) => {
          if (isDrag) {
            modelGroup.rotation.y += (e.clientX - lx) * 0.005;
            lx = e.clientX;
          }
        };
        let tx = 0;
        const onTouchStart = (e: TouchEvent) => {
          if (e.touches.length) tx = e.touches[0].clientX;
        };
        const onTouchMove = (e: TouchEvent) => {
          if (e.touches.length) {
            const cx = e.touches[0].clientX;
            modelGroup.rotation.y += (cx - tx) * 0.008;
            tx = cx;
          }
        };
        const onWheel = (e: WheelEvent) => {
          e.preventDefault();
          camera.position.multiplyScalar(1 + e.deltaY * 0.001);
          camera.position.clampLength(30, 150);
        };

        canvas.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('mousemove', onMouseMove);
        canvas.addEventListener('touchstart', onTouchStart, { passive: true });
        canvas.addEventListener('touchmove', onTouchMove, { passive: true });
        canvas.addEventListener('wheel', onWheel, { passive: false });
        cleanupFns.push(
          () => canvas.removeEventListener('mousedown', onMouseDown),
          () => window.removeEventListener('mouseup', onMouseUp),
          () => canvas.removeEventListener('mousemove', onMouseMove),
          () => canvas.removeEventListener('touchstart', onTouchStart),
          () => canvas.removeEventListener('touchmove', onTouchMove),
          () => canvas.removeEventListener('wheel', onWheel),
        );

        const resize = () => {
          const w = box.clientWidth;
          const h = box.clientHeight;
          if (!w || !h) return;
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          if (w < 600) {
            camera.position.set(0, 45, 95);
            camera.lookAt(0, 5, 0);
          } else {
            camera.position.set(45, 35, 65);
            camera.lookAt(0, 0, 0);
          }
        };
        const ro = new ResizeObserver(resize);
        ro.observe(box);
        window.addEventListener('resize', resize);
        cleanupFns.push(() => ro.disconnect(), () => window.removeEventListener('resize', resize));
        resize();

        buildModel(THREE, modelGroup, stateRef.current);

        const animate = () => {
          frameRef.current = requestAnimationFrame(animate);
          renderer.render(scene, camera);
        };
        animate();

        setReady(true);
      } catch {
        setFailed(true);
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(frameRef.current);
      cleanupFns.forEach((fn) => fn());
      if (modelGroupRef.current) disposeGroup(modelGroupRef.current);
      rendererRef.current?.dispose();
    };
  }, []);

  // Перестроение модели при изменении параметров
  useEffect(() => {
    const THREE = threeRef.current;
    const mg = modelGroupRef.current;
    if (THREE && mg) buildModel(THREE, mg, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.width, state.length, state.height, state.roof, state.ral, state.windows, state.gates]);

  if (failed) {
    return <>{fallback}</>;
  }

  return (
    <div ref={containerRef} className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-[#dbeafe]">
      {!ready && fallback && (
        <div className="absolute inset-0 flex items-center justify-center">{fallback}</div>
      )}
      <canvas
        ref={canvasRef}
        className={`block h-full w-full cursor-grab transition-opacity active:cursor-grabbing ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {ready && (
        <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-white/85 px-3 py-1 text-xs text-slate-600 shadow-sm">
          Вращение: ЛКМ / палец · зум: колесо
        </div>
      )}
    </div>
  );
});

export default Building3D;
